import { defineQuery } from '@rocicorp/zero';
import * as z from 'zod';
import { zql } from '../schema.js';
import { applyCursor } from '../util/apply-cursor.js';
import { Status } from '../util/schemas.js';
import { Cursor, Sort } from './util.js';
import { GroupType } from '../database/schema.js';
export const ListUsersFiltersInput = z
    .object({
    name: z.string().optional(),
    groups: z.array(z.string()).optional(),
    sections: z.array(z.string()).optional(),
    wards: z.array(z.string()).optional(),
    status: z.array(Status).optional(),
    groupTypes: z.array(GroupType).optional(),
})
    .optional();
export const GetUsersByLocation = z.object({
    sections: z.array(z.string()).optional(),
    wards: z.array(z.string()).optional(),
    groupTypes: z.array(GroupType).optional(),
});
export const ListUsersInput = z.object({
    cursor: Cursor.extend({
        createdAt: z.number(),
        firstName: z.string().optional(),
        lastName: z.string().optional(),
        username: z.string().optional(),
        status: z.string().optional(),
    }).optional(),
    sort: Sort.extend({ field: z.string() }),
    limit: z.number().optional(),
    filters: ListUsersFiltersInput,
});
export const usersQueries = {
    listUsers: defineQuery(ListUsersInput, ({ args }) => {
        let q = zql.user.where('deletedAt', 'IS', null);
        if (args.filters) {
            const { name, sections, wards, groups, status, groupTypes } = args.filters;
            // every word must match one of the name fields or the username, in any order
            const terms = name?.split(/[\s,]+/).filter(Boolean) ?? [];
            if (terms.length) {
                q = q.where(({ and, or, cmp }) => and(...terms.map(term => or(cmp('name', 'ILIKE', `%${term}%`), cmp('firstName', 'ILIKE', `%${term}%`), cmp('lastName', 'ILIKE', `%${term}%`), cmp('username', 'ILIKE', `%${term}%`)))));
            }
            if (status?.length) {
                q = q.where('status', 'IN', status);
            }
            if (groups?.length) {
                q = q.whereExists('groups', q => q.where(({ and, cmp }) => and(cmp('id', 'IN', groups))));
            }
            if (groupTypes?.length) {
                q = q.whereExists('groups', q => q.where(({ and, cmp }) => and(cmp('type', 'IN', groupTypes))));
            }
            if (sections?.length || wards?.length) {
                q = q.whereExists('userAccessLocations', q => q.where(({ or, cmp }) => {
                    const conditions = [];
                    if (sections?.length)
                        conditions.push(cmp('sectionId', 'IN', sections));
                    if (wards?.length)
                        conditions.push(cmp('wardId', 'IN', wards));
                    return or(...conditions);
                }), { flip: true });
            }
        }
        q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
        return q.related('groups');
    }),
    getUsersByLocation: defineQuery(GetUsersByLocation, ({ args }) => {
        let q = zql.user.where('deletedAt', 'IS', null);
        if (args) {
            const { sections, wards, groupTypes } = args;
            if (sections?.length || wards?.length) {
                q = q.whereExists('userAccessLocations', q => q.where(({ or, cmp }) => {
                    const conditions = [];
                    if (args?.sections?.length)
                        conditions.push(cmp('sectionId', 'IN', args.sections));
                    if (args?.wards?.length)
                        conditions.push(cmp('wardId', 'IN', args.wards));
                    return or(...conditions);
                }), { flip: true });
            }
            if (groupTypes?.length) {
                q = q.whereExists('groups', g => g.where('type', 'IN', groupTypes).where('deletedAt', 'IS', null), { flip: true });
            }
        }
        q = q.orderBy('firstName', 'asc').orderBy('lastName', 'asc');
        return q;
    }),
    getUserById: defineQuery(z.string(), ({ args }) => {
        let q = zql.user
            .related('created')
            .related('sessions')
            .related('groups', g => g.where('deletedAt', 'IS', null))
            .related('address')
            .related('userAccessLocations', q => q.related('location').related('section').related('ward'))
            .where('id', args)
            .where('deletedAt', 'IS', null)
            .one();
        return q;
    }),
    getUserCredentialAccount: defineQuery(z.string(), ({ args }) => {
        return zql.account.where('userId', args).where('providerId', 'credential').one();
    }),
    getSessionByUserId: defineQuery(z.string(), ({ args }) => {
        let q = zql.session.where('userId', args).one();
        return q;
    }),
    getUserActivitiesById: defineQuery(z.string(), ({ args }) => {
        return zql.eventLogs
            .related('actor')
            .where('resource', '=', 'user')
            .where('resourceId', '=', args)
            .orderBy('performedAt', 'desc');
    }),
};
//# sourceMappingURL=user.queries.js.map